import { useEffect } from "react";

/**
 * Lightweight document.head patcher — overrides the <title> and the main
 * meta tags (description, OG, Twitter) for a given route.
 *
 * We DON'T pull in react-helmet to keep the bundle small: Google Bot
 * executes JS so SEO is preserved, and for social-share crawlers
 * (WhatsApp/Facebook/iMessage…) we already serve participant-focused
 * defaults in `public/index.html`. This hook only refines the experience
 * for human browsers opening the B2B landing / merchant-funnel pages.
 *
 * @param {{ title?: string, description?: string, ogTitle?: string,
 *           ogDescription?: string }} opts
 */
export function usePageMeta(opts) {
  useEffect(() => {
    const { title, description, ogTitle, ogDescription } = opts || {};
    const prevTitle = document.title;
    const prevDesc = _getMetaContent('meta[name="description"]');
    const prevOgTitle = _getMetaContent('meta[property="og:title"]');
    const prevOgDesc = _getMetaContent('meta[property="og:description"]');
    const prevTwTitle = _getMetaContent('meta[name="twitter:title"]');
    const prevTwDesc = _getMetaContent('meta[name="twitter:description"]');

    if (title) document.title = title;
    _setMeta('meta[name="description"]', description);
    _setMeta('meta[property="og:title"]', ogTitle || title);
    _setMeta('meta[property="og:description"]', ogDescription || description);
    _setMeta('meta[name="twitter:title"]', ogTitle || title);
    _setMeta('meta[name="twitter:description"]', ogDescription || description);

    return () => {
      // Restore on unmount so other routes keep their own meta.
      document.title = prevTitle;
      _setMeta('meta[name="description"]', prevDesc);
      _setMeta('meta[property="og:title"]', prevOgTitle);
      _setMeta('meta[property="og:description"]', prevOgDesc);
      _setMeta('meta[name="twitter:title"]', prevTwTitle);
      _setMeta('meta[name="twitter:description"]', prevTwDesc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opts?.title,
    opts?.description,
    opts?.ogTitle,
    opts?.ogDescription,
  ]);
}

function _getMetaContent(selector) {
  const el = document.querySelector(selector);
  return el ? el.getAttribute("content") : null;
}

function _setMeta(selector, value) {
  if (value == null) return;
  const el = document.querySelector(selector);
  if (el) el.setAttribute("content", value);
}
